import React, { useContext, useState } from 'react';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';

import ComponentErrorBoundary from 'components/ComponentErrorBoundary';
import Message from 'components/Message';
import { ColumnLayout, Column, Title, Controls, Control } from 'features/App';
import ScheduleDateControl from 'features/Schedules/ScheduleCalendar/ScheduleDateControl';
import { useIsScheduleCalendarV2Enabled } from 'hooks/useIsScheduleCalendarV2Enabled';
import { useIsScheduleFiltersV2Enabled } from 'hooks/useIsScheduleFiltersV2Enabled';

import VenueSchedulesContext from './Contexts/VenueSchedulesContext';
import EmptyDetail from './EmptyDetail';
import ScheduleDetail from './ScheduleDetailContainer';
import ScheduleFilterChipsContainer from './ScheduleFilterChipsContainer';
import { ScheduleList } from './ScheduleList';
import ScheduleListFilterContainer from './ScheduleListFilterContainer';
import ScheduleTodayButton from './ScheduleTodayButton';
import { VenueSmartSpotStatus } from './SmartSpot';

const messages = defineMessages({
  title: {
    id: 'yield-management.header.title',
    defaultMessage: 'Classes',
  },
});

const YieldManagement = ({ venueId, startDateFormatted, updateStartDate }) => {
  const { activeSchedule: selectedSchedule } = useContext(VenueSchedulesContext);
  const [scrollPos, setScrollPos] = useState(0);
  const isScheduleCalendarV2Enabled = useIsScheduleCalendarV2Enabled(venueId);
  const isScheduleFiltersV2Enabled = useIsScheduleFiltersV2Enabled(venueId);

  const listOnFocusEnter = () => {
    if (scrollPos >= 0) {
      window.scrollTo(0, scrollPos);
    }
  };
  const listOnFocusLeave = () => {
    setScrollPos(window.scrollY);
  };

  const todayButton = isScheduleCalendarV2Enabled === true ? <ScheduleTodayButton venueId={venueId} /> : null;
  const controlsClassName = isScheduleFiltersV2Enabled === true ? 'yield-management__controls--filters-v2' : undefined;

  return (
    <ColumnLayout>
      <Column
        className="column-layout__column--lg-5/12 yield-management__list-column"
        name="list"
        type="flush"
        onFocusEnter={listOnFocusEnter}
        onFocusLeave={listOnFocusLeave}>
        <Message message={messages.title} as={Title} />
        <Controls className={controlsClassName}>
          <Control type="filter" className={todayButton ? 'schedule-date-controls' : undefined}>
            <ScheduleDateControl setDate={updateStartDate} venueId={venueId} />
            {todayButton}
          </Control>
          {isScheduleFiltersV2Enabled !== true && (
            <Control type="filter">
              <ScheduleListFilterContainer />
            </Control>
          )}
          <Control type="filter" className="schedule-smartspot-control">
            <VenueSmartSpotStatus className="yield-management__smartspot-toggle" />
          </Control>
          {isScheduleFiltersV2Enabled === true && (
            <Control type="trough">
              <ScheduleFilterChipsContainer />
            </Control>
          )}
        </Controls>
        <ComponentErrorBoundary>
          <ScheduleList
            key={`${venueId}-${startDateFormatted}`}
            className="yield-management__schedule-list"
            venueId={venueId}
          />
        </ComponentErrorBoundary>
      </Column>
      <Column className="column-layout__column--lg-7/12 lg-pl-l" name="detail">
        <Controls />
        <ComponentErrorBoundary>
          {_isEmpty(selectedSchedule) ? <EmptyDetail /> : <ScheduleDetail key={selectedSchedule.scheduleId} />}
        </ComponentErrorBoundary>
      </Column>
    </ColumnLayout>
  );
};

YieldManagement.propTypes = {
  venueId: PropTypes.number,
  startDateFormatted: PropTypes.string,
  updateStartDate: PropTypes.func,
};

export default YieldManagement;
